import {
  useFeaturesContext,
  usePreferencesContext,
  User,
  useThemeContext,
  useUserContext,
  useUser,
} from 'contexts';
import { getListingById } from 'data/listing';
import { createSearchPredictionModel, MapType } from 'data/search-predictions';
import useLocationSearch from 'hooks/use-location-search';
import useMapLocationChanger from 'hooks/use-map-location-changer';
import { useIsIpadMini, useIsMobile } from 'hooks/use-size-class';
import { useSearchPlaceholder } from 'hooks/use-search-placeholder';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router'; // Ensure useRouter is imported
import { useCallback, useEffect, useRef, useState } from 'react';
import { SEARCH_OPTION_AGENTS, SEARCH_OPTION_BUY, SEARCH_OPTION_HOME_APPRAISAL, SEARCH_OPTION_RENT, SEARCH_OPTION_SOLD } from '@tenants/types/theme';
import { ThemeNames } from 'types/themes';
import HomeAppraisalStorage from 'utils/appraisal-storage';
import { findPlaceByPlaceId } from 'utils/google-maps/geoLocator';
import { isCanadianProvinceCode, ProvinceOrStateCode } from 'utils/province_or_state';
import { getSearchPredictionSortOptions, sortSearchPredictions } from 'components/suggested-location-dropdown';
import { getLastSearchDescription } from 'utils/search-predictions';
import { SearchStrategyFactory } from 'strategy/search/SearchStrategyFactory';

import type { SearchAgentPrediction, SearchSuggestions } from 'components/suggested-location-dropdown';
import type ListingParams from 'contexts/preferences/listing-params';
import type SearchPrediction from 'data/search-predictions';
import type { SearchOptions } from '@tenants/types/theme';
import { AGENT_SEARCH_PATH } from 'utils/agent-endpoint';
import { isExpTheme } from 'utils/tenant/which-tenant';
import { BUY_FILTER_OPTION, RENT_FILTER_OPTION, SOLD_FILTER_OPTION } from 'constants/area-map-page-filters';
import { AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS } from 'contexts/preferences/listing-params';
import { AdvancedSearchProps } from './advanced-search';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_ALL_HEADER_SEARCH_BUTTON_CLICK, GTM_CLICK_APPRAISAL_GOOGLE_ADDRESS, GTM_CLICK_APPRAISAL_HOME_SEARCH_BAR, GTM_CLICK_APPRAISAL_HOME_SEARCH_RESULT, GTM_CLICK_APPRAISAL_LISTING_ADDRESS, GTM_CLICK_HOMEPAGE_SEARCH_BAR, GTM_CLICK_HOMEPAGE_SEARCH_BUTTON } from 'constants/events';
import { widgetEvents } from 'utils/widget/analytics';

// Proper dynamic import at module level
const SuggestedLocationDropdown = dynamic(() => import('components/suggested-location-dropdown'), {
  loading: () => null,
  ssr: false,
});

interface HandleLocationSearchProps {
  searchPrediction: SearchPrediction;
  listingParams: ListingParams;
  user: User | null;
  map?: MapType;
  addToRecentSearches: (searchPrediction: SearchPrediction) => void;
  setActivePrediction?: (searchPrediction: SearchPrediction) => void;
  themeName: ThemeNames;
  forceAreapage?: boolean;
}

type Props = AdvancedSearchProps & {
  isInPanel?: boolean;
  forceModal?: boolean;
};

export const handleSearchResult = ({ searchPrediction, listingParams, user, map, addToRecentSearches, setActivePrediction, themeName, forceAreapage = false } : HandleLocationSearchProps) => {
  const prediction: SearchPrediction = !searchPrediction.transitionToPath ? createSearchPredictionModel(searchPrediction) : searchPrediction;
  setActivePrediction?.(prediction);
  prediction.transitionToPath(listingParams, themeName, map, user, addToRecentSearches, forceAreapage);
};

const useSearchBar = ({ isInPanel = false, onValueChange, forceModal = false }: Props) => {
  const { features, setIsSearchPanelOpen } = useFeaturesContext();
  const { siteLocation } = useUser();
  const router = useRouter();
  const isHomePage = router.pathname === '/';
  const isWidget = router.pathname && router.pathname.startsWith('/widget/');
  const isHomeAppraisal = router.pathname && (router.pathname.startsWith('/home-appraisal') || router.pathname.startsWith('/widget/home-appraisal'));
  const { user } = useUserContext();
  const { listingParams, addToRecentSearches, lastSearchLocation } = usePreferencesContext();
  const { theme, themeName } = useThemeContext();
  const [activePrediction, setActivePrediction] = useState<SearchPrediction | SearchSuggestions>();
  const [isLocationDropdownActive, setIsLocationDropdownActive] = useState(isInPanel);
  const [onSearchPage, setOnSearchPage] = useState(false);
  const isMobile = useIsMobile();
  const isIpadMini = useIsIpadMini();
  const [locationQuery, setLocationQuery] = useState(lastSearchLocation ? getLastSearchDescription(lastSearchLocation): '');
  // When user clicks on search inputs and it transforms to full screen search it looses the search options state
  const [currentSearchOptions, setCurrentSearchOptions] = useState<SearchOptions>(isHomeAppraisal ? SEARCH_OPTION_HOME_APPRAISAL : SEARCH_OPTION_BUY);
  const { isLoadingSearchPredictions, searchPredictions, searchAgentPredictions, isTyping, waitForPredictionsToLoad } = useLocationSearch({
    queryString: locationQuery,
    useUsListings: features.useUsListings,
    searchOptions: currentSearchOptions,
  });
  const isExpTenant = isExpTheme(themeName);
  const disablePopularSearches = currentSearchOptions.match(new RegExp(`${SEARCH_OPTION_AGENTS}|${SEARCH_OPTION_HOME_APPRAISAL}`, 'g'));
  const textInputRef = useRef<HTMLInputElement>(null);
  const { getPlaceholder } = useSearchPlaceholder();

  const map: MapType = useMapLocationChanger();

  const searchPlaceholder = getPlaceholder(currentSearchOptions);

  const hideSearchPanel = useCallback(() => {
    setIsSearchPanelOpen(false);
  }, [setIsSearchPanelOpen]);

  const dismissSearchPanel = useCallback(() => {
    hideSearchPanel();
    setIsLocationDropdownActive(false);
  }, [hideSearchPanel]);

  const doOnSearchOptionChanged = useCallback((value: SearchOptions) => {
    setCurrentSearchOptions(value);
    if (onValueChange) {
      if (value === SEARCH_OPTION_BUY) {
        onValueChange({ status: AVAILABLE_STATUS, rental: false }, BUY_FILTER_OPTION);
      } else if (value === SEARCH_OPTION_RENT) {
        onValueChange({ status: AVAILABLE_STATUS, rental: true }, RENT_FILTER_OPTION);
      } else if (value === SEARCH_OPTION_SOLD) {
        onValueChange({ status: NOT_AVAILABLE_SOLD_STATUS, rental: false }, SOLD_FILTER_OPTION);
      }
    } else {
      if (value === SEARCH_OPTION_BUY) {
        listingParams.setRental(false);
        listingParams.setStatus(AVAILABLE_STATUS);
      } else if (value === SEARCH_OPTION_RENT) {
        listingParams.setRental(true);
        listingParams.setStatus(AVAILABLE_STATUS);
      } else if (value === SEARCH_OPTION_SOLD) {
        listingParams.setRental(false);
        listingParams.setStatus(NOT_AVAILABLE_SOLD_STATUS);
      }
    }
  }, [listingParams, onValueChange]);

  const doOnSearchResultClicked = useCallback(async (searchPrediction: SearchPrediction | SearchAgentPrediction) => {
    isHomePage && trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_BUTTON);
    // UiAllSrcBSbt with search text is sent from useSearchBarTracking.markSearchAsSubmitted (submit / Enter / search button).
    // Do not fire UiAllSrcBSbt here — a second push with no label overwrites the dataLayer and GA4 can show null label.
    if (isHomeAppraisal) {
      trackEvent(GTM_CLICK_APPRAISAL_HOME_SEARCH_RESULT);
      const eventName = (searchPrediction.group === 'locations') ? GTM_CLICK_APPRAISAL_GOOGLE_ADDRESS : GTM_CLICK_APPRAISAL_LISTING_ADDRESS;
      trackEvent(eventName);
    }
    if (currentSearchOptions === SEARCH_OPTION_AGENTS) {
      const locationArray = (searchPrediction as SearchAgentPrediction).label.split(',');
      const trimmedLocationArray = locationArray.map(part => part.trim());
      const city = trimmedLocationArray[trimmedLocationArray.length - 2];
      const provinceOrState = trimmedLocationArray[trimmedLocationArray.length - 1];
      const country = isCanadianProvinceCode(provinceOrState.toLowerCase() as ProvinceOrStateCode) ? 'CA' : 'US';
      router.push(`${AGENT_SEARCH_PATH}/?location=${city}%2C+${provinceOrState}&country=${country}`);
    } else if (currentSearchOptions === SEARCH_OPTION_HOME_APPRAISAL) {
      const isListingPrediction = searchPrediction.group == 'listings';
      if (isListingPrediction) {
        const listingId = (searchPrediction as SearchPrediction).id?.split('-')?.[1];
        const listing = await getListingById(listingId);
        HomeAppraisalStorage.setProperty(listing);
      } else { // Means we got location prediction from Google
        const data = await findPlaceByPlaceId((searchPrediction as SearchPrediction).id);
        if (data) HomeAppraisalStorage.setProperty(data);
      }
      const appraisalBasePath = isWidget ? '/widget/home-appraisal' : '/home-appraisal';
      const widgetQuery = isWidget && router.query.clientId ? `?clientId=${encodeURIComponent(router.query.clientId as string)}` : '';
      if (isWidget && router.query.clientId) {
        widgetEvents.trackSearchClick(router.query.clientId as string);
      }
      router.push(`${appraisalBasePath}/property-details${widgetQuery}`);
    } else {
      handleSearchResult({
        searchPrediction: searchPrediction as SearchPrediction,
        listingParams,
        user,
        map: onSearchPage ? map : undefined,
        addToRecentSearches,
        setActivePrediction,
        themeName: themeName as ThemeNames,
      });
    }
    dismissSearchPanel();
  }, [isHomePage, isHomeAppraisal, isWidget, currentSearchOptions, router, listingParams, user, onSearchPage, map, addToRecentSearches, setActivePrediction, themeName, dismissSearchPanel]);

  const doOnSearchButtonClicked = useCallback(async (query?: string) => {
    const q = (query ?? locationQuery).trim();
    if (!activePrediction || isLoadingSearchPredictions) {
      console.log('Searching for predictions');
      const strategy = SearchStrategyFactory.getStrategy(currentSearchOptions);
      await strategy.search({
        queryString: q,
        useUsListings: features.useUsListings,
        siteLocation,
        themeName: String(themeName),
        features,
      }).then(({ searchPredictions: predictions }) => {
        if (predictions && predictions.length > 0) {
          const sortOptions = getSearchPredictionSortOptions(q, predictions);
          const suggestedResults = sortSearchPredictions(predictions, sortOptions);
          const prioritizedGroupResults = sortOptions.prioritizeListings
            ? [...suggestedResults.listings, ...suggestedResults.locations]
            : [...suggestedResults.locations, ...suggestedResults.listings];
          const flattenedResults = [
            ...suggestedResults.militaryBases,
            ...prioritizedGroupResults,
            ...suggestedResults.schools,
          ];
          doOnSearchResultClicked(flattenedResults[0]);
          dismissSearchPanel();
          return;
        }
      });
    }

    else if (activePrediction) {
      await doOnSearchResultClicked(activePrediction as SearchPrediction);
      dismissSearchPanel();
      return;
    }

    // Fallback: if query equals the last search location, reuse it
    else if (lastSearchLocation && q === lastSearchLocation.description) {
      await doOnSearchResultClicked(lastSearchLocation as SearchPrediction);
      return;
    }
  }, [locationQuery, activePrediction, isLoadingSearchPredictions, lastSearchLocation, dismissSearchPanel, themeName, doOnSearchResultClicked, currentSearchOptions, features, siteLocation]);


  const showSearchSuggestions = useCallback(() => {
    if (forceModal || isIpadMini || !isMobile || isWidget) {
      setIsLocationDropdownActive(true);
    } else {
      setIsSearchPanelOpen(true);
    }
  }, [forceModal, isIpadMini, isMobile, isWidget, setIsSearchPanelOpen]);

  const hideSearchSuggestions = () => {
    setIsLocationDropdownActive(false);
  };

  useEffect(() => {
    // On mobile, the location search is hidden and the user only sees the advanced search. This logic is for if the user is on
    // the map page and they search for a location that has an area page, they should stay on the map instead of being redirected
    // to the area page.
    if (window.location.pathname.startsWith('/search')) {
      setOnSearchPage(true);
    } else {
      setOnSearchPage(false);
    }
    if (window.location.pathname.startsWith('/home-appraisal')) {
      setCurrentSearchOptions(SEARCH_OPTION_HOME_APPRAISAL);
    }
  }, [router.pathname]); // Added router.pathname to dependencies to react to route changes

  const doOnSearchBarClicked = useCallback(() => {
    trackEvent(GTM_CLICK_ALL_HEADER_SEARCH_BUTTON_CLICK);
    isHomePage && trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_BAR);
    isHomeAppraisal && trackEvent(GTM_CLICK_APPRAISAL_HOME_SEARCH_BAR);
    showSearchSuggestions();
  }, [isHomePage, isHomeAppraisal, showSearchSuggestions]);

  return {
    isMobile,
    theme,
    isHomeAppraisal,
    textInputRef,
    isExpTenant,
    SuggestedLocationDropdown,
    searchPlaceholder,
    disablePopularSearches,
    isLocationDropdownActive,
    currentSearchOptions,
    isHomePage,
    onSearchPage,
    searchPredictions:{
      activePrediction,
      setActivePrediction,
      isLoadingSearchPredictions,
      searchPredictions,
      searchAgentPredictions,
      isTyping,
    },
    locationQuery,
    setLocationQuery,
    doOnSearchBarClicked,
    doOnSearchOptionChanged,
    doOnSearchResultClicked,
    doOnSearchButtonClicked,
    waitForPredictionsToLoad,
    showSearchSuggestions,
    hideSearchSuggestions,
    hideSearchPanel,
  };
};

export { useSearchBar };
